import {Suspense, useMemo} from "react";
import {Navigate, useLocation} from "react-router-dom";
import {FlagsProvider} from "flagged";
import {Helmet, HelmetProvider} from "react-helmet-async";

import {useAuth, useCurrentUser} from "~/hooks";
import {AnyObject} from "~/model/helperTypes/generic";
import {useAppSelector, selectMaker} from "~/modules/redux";
import {PublicModals} from "~/containers/modals/PublicModals";
import {AuthRight} from "~/api/generated";

import {HOME_PATH} from "../paths";

export interface PublicRouteWrapperProps {
  SuspenseFallbackComponent: React.ComponentType<any>;
  PageComponent: React.ComponentType<any>;
  componentProps: AnyObject;
  restrictedFromAuthorized?: boolean;
  zendeskRelatedLabels?: string[];
  accessRight?: AuthRight;
}

export const PublicRouteWrapper: React.FC<PublicRouteWrapperProps> = ({
  SuspenseFallbackComponent,
  PageComponent,
  componentProps,
  restrictedFromAuthorized,
  zendeskRelatedLabels,
}) => {
  useCurrentUser();

  const maker = useAppSelector(selectMaker);
  const [isAuthorized] = useAuth();
  const {pathname} = useLocation();

  const features = useMemo(() => {
    let nextFeatures: AnyObject = {
      // possible values: local, staging, production
      [process.env.BEMAKERS_ENV!]: true,
    };

    maker?.features?.forEach((feature) => {
      nextFeatures = {
        ...nextFeatures,
        [feature]: true,
      };
    });

    return nextFeatures;
  }, [maker?.features]);

  if (isAuthorized && restrictedFromAuthorized) {
    return <Navigate to={HOME_PATH} state={{from: pathname}} replace />;
  }

  return (
    <FlagsProvider features={features}>
      <HelmetProvider>
        <Helmet>{zendeskRelatedLabels && <meta name="keywords" content={zendeskRelatedLabels.join(", ")} />}</Helmet>
      </HelmetProvider>
      <Suspense fallback={<SuspenseFallbackComponent />}>
        <PageComponent {...componentProps} />
      </Suspense>
      <PublicModals />
    </FlagsProvider>
  );
};
